import { syntaxTree } from '@codemirror/language';
import { EditorState } from '@codemirror/state';

import { escapeSuggestion } from './escape-suggestion.js';
import { getStatementStatus } from './get-statement-status.js';
import { getTextFromRange } from './get-text.js';
import {
  getStatementCursorAtPosition,
  hasNextSibling,
  hasNextStatement,
} from './tree-navigation.js';
import { isLogicalOperator } from './tree-node-guards.js';
import { filterFieldLanguageExtension } from '../parser/filterfield-language.js';
import type { FilterFieldSuggestionProps } from '../suggestions/Suggestion.js';
import type { _FilterFieldSuggestionsContext } from '../suggestions/suggestions-extension.js';

/** @internal */
export function getSuggestionTransaction(
  suggestionValue: string,
  trailing: string | undefined,
  insertionStrategy: FilterFieldSuggestionProps['insertionStrategy'],
  cursorBeforeTrailing: boolean | undefined,
  suggestionsContext: _FilterFieldSuggestionsContext,
  editorState: EditorState,
) {
  const { token, currentStatement } = suggestionsContext;
  const currentSelection = editorState.selection;
  const { head } = currentSelection.main;

  const inOperator = currentStatement?.operator?.type === 'InclusionOperator';
  const autoEscapeSuggestion =
    insertionStrategy === 'replace-token' &&
    suggestionsContext.shouldEscapeSuggestion;

  const appliedSuggestionValue = `${
    autoEscapeSuggestion ? escapeSuggestion(suggestionValue) : suggestionValue
  }${
    (inOperator &&
    currentStatement.value &&
    currentStatement.value.type !== 'Error'
      ? ''
      : trailing) ?? ''
  }`;
  const trimmedValue = appliedSuggestionValue.trimEnd();
  const isExistsOperator = trimmedValue.match(/^(= \*)$|^(!= \*)$/);
  const addSpace = appliedSuggestionValue.slice(-1) === ' ';

  const currentDocumentLength = editorState.doc.toString().length;

  let replaceFrom = token.range.from;
  let replaceUntil = token.range.to;
  let nextSibling = false;
  let nextStatement = false;
  let startsWithOperator = false;
  let endsWithOperator = false;
  let hasSpaceAfterQuotes = false;
  let insertedValue = appliedSuggestionValue;

  const isInclusionOperatorSuggestion = ['in (', 'not in ('].includes(
    suggestionValue,
  ); // these values come from our defined defaultComparisonOperatorSuggestions values when we have autosuggestions
  const followedByOpeningParenthesis = _isFollowedByOpeningBracket(
    editorState,
    head,
  );

  // If the token at the cursor position is an exists operator, the replacement strategy depends
  // on the exact position of the cursor in the exists.
  // 'foo =| *' -> the whole exists operator is replaced by the suggestion value
  // 'foo = |*' -> the asterisk is replaced by the suggestion value
  // 'foo = *|' -> the suggestion value is inserted after the asterisk
  if (token.type === 'ExistsOperator') {
    if (head === token.range.to) {
      replaceFrom = token.range.to;
    } else if (head === token.range.to - 1) {
      replaceFrom = token.range.to - 1;
    }
  }

  // If the token at the cursor position is a list, only replace single list values if the cursor
  // is positioned inside the list's brackets.
  if (
    token.type === 'List' &&
    head > token.range.from &&
    head < token.range.to &&
    insertionStrategy === 'replace-token'
  ) {
    replaceFrom = head;
    replaceUntil = head;
  }

  nextStatement = hasNextStatement(head, editorState);

  const { value } = currentStatement ?? {};
  const isValueToken =
    token.range.from === value?.range.from &&
    token.range.to === value?.range.to;

  // If the current statement is undefined, it is definitely not followed by any
  // other node and we want to automatically add the default trailing space
  // (unless overridden) when applying a suggestion.
  // If there is a statement, we want to override the added space in certain cases.
  if (currentStatement) {
    nextSibling = !nextStatement
      ? hasNextSibling(head, editorState, { ignorePositionOperators: true })
      : nextStatement;
    startsWithOperator =
      isValueToken &&
      currentStatement.operator?.type === 'ComparisonOperator' &&
      (currentStatement.operator.value === 'starts-with' ||
        currentStatement.operator.value === 'contains');
    endsWithOperator =
      isValueToken &&
      currentStatement.operator?.type === 'ComparisonOperator' &&
      (currentStatement.operator.value === 'ends-with' ||
        currentStatement.operator.value === 'contains');

    // If the applied suggestion is an exists operator (= * | != *), we want to also
    // replace the value of the current filter statement.
    // e.g.:
    //   foo =| bar
    //   insert "= *"
    //   foo = *
    if (
      insertionStrategy === 'replace-statement' ||
      (isExistsOperator &&
        currentStatement.value &&
        !followedByOpeningParenthesis) ||
      startsWithOperator
    ) {
      nextSibling = nextStatement;
      replaceUntil = currentStatement.range.to;
    }

    insertedValue =
      nextSibling || insertionStrategy === 'insert'
        ? trimmedValue
        : appliedSuggestionValue;
    const containsOperator = startsWithOperator && endsWithOperator;

    // For inclusion operators, we need to change the range with the cursor positioned
    // in the value when applying an inclusion operator suggestion, e.g. foo in |(
    // Applying the suggestion should replace the operator too, not only the value.
    if (
      isInclusionOperatorSuggestion &&
      inOperator &&
      (currentStatement.value?.value?.toString() ?? '') === ''
    ) {
      replaceFrom = currentStatement.operator?.range.from ?? replaceFrom;
    }

    // When replacing a value that has a starts-with operator, we want to replace
    // the value while keeping the operator. If a space is supposed to be added, we
    // have to add it after the position operator.
    if (
      (startsWithOperator || endsWithOperator) &&
      insertionStrategy === 'replace-token'
    ) {
      let { from, to } = {
        from: currentStatement.range.to,
        to: currentStatement.range.to,
      };

      if (currentStatement.value) {
        ({ from, to } = currentStatement.value.range);
      }

      if (
        startsWithOperator &&
        ((head > from && head < to) || (!containsOperator && head === from))
      ) {
        startsWithOperator = false;
        insertedValue = `${trimmedValue}*${
          !nextSibling && addSpace ? ' ' : ''
        }`;
      } else {
        if (head === from || (startsWithOperator && head === to)) {
          replaceFrom = from;
          insertedValue = appliedSuggestionValue;
        }

        replaceUntil = to;
        startsWithOperator = false;
        nextSibling = nextStatement;
      }
    }
  } else {
    insertedValue = nextSibling ? trimmedValue : appliedSuggestionValue;
  }

  let range: { from: number; to: number };

  switch (insertionStrategy) {
    case 'insert':
      range = {
        from: head,
        to: head,
      };
      break;
    case 'replace-statement': {
      const editorState = EditorState.create({
        doc: appliedSuggestionValue,
        extensions: [filterFieldLanguageExtension],
      });
      const suggestionCursor = getStatementCursorAtPosition(
        appliedSuggestionValue.length,
        editorState,
      );

      if (getStatementStatus(suggestionCursor) !== 'complete') {
        insertedValue = appliedSuggestionValue;
        nextSibling = false;
      }

      range = {
        from: currentStatement?.range.from ?? head,
        to: currentStatement?.range.to ?? head,
      };

      if (isLogicalOperator(token)) {
        nextSibling = nextStatement;
        if (nextSibling) {
          insertedValue = trimmedValue;
        }

        range = {
          from: token.range.from,
          to: token.range.to,
        };
      }
      break;
    }
    case 'replace-all':
      // Reset inserted value to the initial suggestion value, including all trailing characters.
      insertedValue = appliedSuggestionValue;
      nextSibling = false;
      range = {
        from: 0,
        to: editorState.doc.toString().length,
      };
      break;
    default: {
      if (
        token.type === 'String' ||
        token.type === 'Key' ||
        token.type === 'Error'
      ) {
        // The current token may end before the enclosing statement, e.g. '"foo"| ' -> current token '"foo"' doesn't
        // include the space, which is included in the statement's range.
        // We need to check whether there's a space after the current token to properly insert the suggestion.
        if (token.range.to < (currentStatement?.range.to ?? 0)) {
          const characterAfterToken = getTextFromRange(
            {
              from: token.range.to,
              to: token.range.to + 1,
            },
            editorState.doc,
          );

          // If there's already a space after the current token, handle insertion as if there was a next sibling.
          if (characterAfterToken === ' ') {
            nextSibling = true;
            insertedValue = trimmedValue;
          }
        }

        if (head > token.range.from && head < token.range.to) {
          const isTokenEscaped = token.textValue.match(/^"[^"]*"$/);
          const isAppliedSuggestionValueEscaped =
            appliedSuggestionValue.match(/^"[^"]*"$/) ??
            appliedSuggestionValue.match(/^"[^"]*" $/);

          if (isTokenEscaped && !isAppliedSuggestionValueEscaped) {
            replaceFrom = token.range.from + 1;
            replaceUntil = token.range.to;

            const addSpace = insertedValue.slice(-1) !== ' ';

            insertedValue = addSpace ? `${trimmedValue}" ` : `${trimmedValue}"`;
            hasSpaceAfterQuotes = addSpace;
          }
        }
      }

      const value = currentStatement?.value;
      let stringifiedValue = value?.textValue;

      // Handle insertion of parentheses for inclusion operator suggestions.
      // If an operator is already present, depending on the rest of the statement,
      // we need to adjust the inserted value and range accordingly.
      if (isInclusionOperatorSuggestion && currentStatement?.operator) {
        // Already have an opening parenthesis and some value after that
        // -> remove the closing parenthesis and only replace until the beginning
        //    of the already present value, moving the cursor to it's start.
        if (followedByOpeningParenthesis && stringifiedValue !== undefined) {
          replaceFrom = currentStatement.operator.range.from;
          replaceUntil = currentStatement?.value?.range.to ?? replaceUntil;
          insertedValue = insertedValue.replace('(', '');
          insertedValue = insertedValue.replace(')', '');

          if (stringifiedValue?.at(-1) !== ')') {
            // If the stringified value does not end with a closing parenthesis, we add it
            stringifiedValue = stringifiedValue + ')';
          }

          trailing = '';
          insertedValue = `${insertedValue}${stringifiedValue}`;
        } else if (
          (followedByOpeningParenthesis && stringifiedValue === undefined) ||
          stringifiedValue === ''
        ) {
          replaceUntil = currentStatement.value?.range.to ?? replaceUntil;
        }
      }

      range = {
        from: Math.min(currentDocumentLength, replaceFrom),
        to: Math.min(currentDocumentLength, replaceUntil),
      };
    }
  }

  let anchor =
    insertionStrategy === 'insert'
      ? head + insertedValue.length
      : Math.min(currentDocumentLength, range.from) +
        (nextSibling ? appliedSuggestionValue.length : insertedValue.length) +
        (insertionStrategy === 'replace-token' ? +startsWithOperator : 0) +
        +hasSpaceAfterQuotes;

  if (cursorBeforeTrailing) {
    const trailingLength = trailing ? trailing.length : 0;
    anchor -= trailingLength + (autoEscapeSuggestion ? 1 : 0);
  }

  return {
    changes: {
      ...range,
      insert: insertedValue,
    },
    selection: {
      anchor,
    },
  };
}

/**
 * Check if the next node after cursor position contains an opening bracket.
 * @internal
 */
export function _isFollowedByOpeningBracket(
  editorState: EditorState,
  head: number,
) {
  const tree = syntaxTree(editorState);

  let cursor = tree.cursorAt(head, 1);

  if (cursor.name === 'OpeningBracket') {
    return true;
  }

  if (cursor.node.name === 'Filter' || cursor.node.name === 'FilterStatement') {
    cursor = tree.cursorAt(head, -1);
  }

  if (!cursor.next()) {
    return false;
  }

  if (cursor.name === 'OpeningBracket' && cursor.node.from >= head) {
    return true;
  }

  if (cursor.name === '⚠') {
    if (cursor.next() && cursor.node.from >= head) {
      // the first check handles cases like `foo = (bar` and the FilterGroup check handles cases like `foo = (bar, baz)`, that already includes an opening bracket
      // @ts-expect-error the cursor name type changed by calling next, but typescript doesn't recognize it
      if (cursor.name === 'OpeningBracket' || cursor.name === 'FilterGroup') {
        return true;
      }
    }
  }

  if (cursor.name === 'FilterIncludesValue') {
    if (cursor.next() && cursor.node.from >= head) {
      // if the node is a list node, we know it already includes an opening bracket and otherwise we check if it's just an opening bracket node
      // @ts-expect-error the cursor name type changed by calling next, but typescript doesn't recognize it
      if (cursor.name === 'OpeningBracket' || cursor.name === 'List') {
        return true;
      }
    }
  }

  // keep track of the cursor at the next position for cases like (a = 1) (a = b) at index 6 or foo = bar (test, bla) at index 8
  const cursorAtExactPosition = tree.cursorAt(head + 1, 1);

  if (cursorAtExactPosition.node.name === 'OpeningBracket') {
    return true;
  }

  cursorAtExactPosition.parent();

  if (
    cursorAtExactPosition.node.name !== 'Filter' &&
    cursor.node.name === 'FilterGroup'
  ) {
    return true;
  }

  return false;
}
